import { useEffect, useRef } from "react";
import { useVerificationKeystrokes } from "../hooks/useVerificationKeystrokes";
import { opsTickClient } from "@/lib/ops-tick-client";
import { api } from "@/lib/api-client";

interface Props {
  documentId: string;
  step: string;
  /** Whether this station currently owns the document lock. */
  active: boolean;
  /** Current user id (from useAuth). Nullable while auth state is loading. */
  operatorId: string | null;
  /**
   * R-235 B2 — graceful-bump guard. When a supervisor moves the operator's
   * current doc, the VS sets this ref's `.current = true` synchronously (in
   * useDocumentMovedBump's onBump) BEFORE navigate(). The unmount flush below
   * reads `.current` at cleanup time — which is fresh because it's set after
   * the last render — and skips the keystroke-snapshot POST so the dropped
   * in-progress entry is NOT persisted. A plain boolean prop would be stale
   * at cleanup (captured at last render, before onBump ran), so a ref is
   * required. Optional: callers without bump semantics omit it.
   */
  skipFinalFlushRef?: React.RefObject<boolean | null>;
}

/**
 * Headless subcomponent that owns the Phase 18 keystroke recording
 * lifecycle for a document. Extracted from VerificationStation.tsx
 * following the VerificationCheckInButton precedent so the behavior can
 * be unit-tested in isolation (VerificationStation.tsx is too
 * dependency-heavy to mount in tests).
 *
 * Responsibilities:
 *   1. Host useVerificationKeystrokes (counts, clamp, idle).
 *   2. Fire a 10s setInterval that POSTs deltas to /api/ops/operator-tick.
 *   3. Flush a final snapshot to /api/documents/:id/keystroke-snapshot on
 *      unmount (step transition, navigation, etc.) — only when keystrokes
 *      actually accrued, so we don't POST empty payloads.
 *
 * When `active=false` (station does not own the lock) the component is
 * fully inert: no ticks, no snapshot, no event listeners. Returns null —
 * it renders nothing.
 */
export function VerificationKeystrokeRecorder({
  documentId,
  step,
  active,
  operatorId,
  skipFinalFlushRef,
}: Props) {
  const keystrokeState = useVerificationKeystrokes({
    documentId,
    step,
    active,
  });

  // Ref keeps latest state accessible to timer + unmount callbacks without
  // recreating them on every keystroke.
  const stateRef = useRef(keystrokeState);
  stateRef.current = keystrokeState;

  // Delta baseline for the 10s tick — tracks what we've already reported.
  const lastSentRef = useRef({ keystrokeCount: 0, activeKeyingMs: 0 });

  // 10s tick loop — POSTs deltas since the last tick.
  useEffect(() => {
    if (!active) return;
    const interval = setInterval(() => {
      const ks =
        stateRef.current.keystrokeCount - lastSentRef.current.keystrokeCount;
      const ms =
        stateRef.current.activeKeyingMs - lastSentRef.current.activeKeyingMs;
      if (ks === 0 && ms === 0) return;
      lastSentRef.current = {
        keystrokeCount: stateRef.current.keystrokeCount,
        activeKeyingMs: stateRef.current.activeKeyingMs,
      };
      const kpmSample = ms > 0 ? Math.round((ks / ms) * 60_000) : 0;
      void opsTickClient.flush({
        documentId,
        step,
        keystrokeDelta: ks,
        activeKeyingDeltaMs: ms,
        kpmSample,
      });
    }, 10_000);
    return () => clearInterval(interval);
  }, [documentId, step, active]);

  // Unmount flush — fire a final snapshot when the station tears down the
  // recorder (step transition, doc change, navigation). Only POST when we
  // actually recorded keystrokes to avoid noise in the DocumentStats trail.
  // Ref keeps the skip-flush guard fresh at cleanup time (R-235 B2). Captured
  // into a ref-of-ref so the cleanup closure always reads the latest prop ref.
  const skipFinalFlushRefRef = useRef(skipFinalFlushRef);
  skipFinalFlushRefRef.current = skipFinalFlushRef;

  useEffect(() => {
    if (!active) return;
    return () => {
      // R-235 B2 — supervisor moved this doc: the in-progress unsaved entry is
      // dropped, so skip the final snapshot POST. The ref's `.current` is set
      // synchronously in the VS's onBump BEFORE navigate(), so it's fresh here.
      if (skipFinalFlushRefRef.current?.current) return;
      const s = stateRef.current;
      if (s.keystrokeCount === 0) return;
      const fieldCount = Math.max(1, s.fieldTimings.size);
      const timePerFieldMs = Math.round(s.activeKeyingMs / fieldCount);
      void api
        .post(`/documents/${documentId}/keystroke-snapshot`, {
          keystrokeCount: s.keystrokeCount,
          activeKeyingMs: s.activeKeyingMs,
          idleMs: s.idleMs || null,
          timePerFieldMs,
          stepTrailDelta: {
            step,
            operatorId: operatorId ?? null,
            keystrokeCount: s.keystrokeCount,
            activeKeyingMs: s.activeKeyingMs,
            fieldsTouched: s.fieldTimings.size,
          },
        })
        .catch(() => void 0);
    };
  }, [documentId, step, active, operatorId]);

  return null;
}
